import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { computeVirtualWindow } from '~/pages/SpotBidding/models/virtualWindow'

/** 実測前に表示領域の高さとみなす値 (= スクロール領域の max-height)。 */
const INITIAL_VIEWPORT_HEIGHT = 360
/** 測れていない行の高さの既定の推定値 (px)。 実測が溜まれば実測の平均に切り替える。 */
const DEFAULT_ROW_HEIGHT = 72
/** 表示領域の外に余分に描く行数 (スクロール中に空白が見えないように)。 */
const OVERSCAN_ROWS = 6

export type VirtualRows = {
  scrollRef: React.RefObject<HTMLDivElement>
  onScroll: () => void
  /** 描く行の範囲 [start, end)。 */
  start: number
  end: number
  /** 描かない行の高さの合計 (上 / 下、 px)。 */
  topPad: number
  bottomPad: number
  /** 描いた行が自分の高さを知らせる。 */
  measure: (id: string, height: number) => void
}

/**
 * 縦に長い表で、 見えている行だけを描くための状態。 行の高さは内容で変わるので実測し、
 * 測れていない行は推定値で位置を決める。 レイアウトを持たない環境 (表示領域の高さが 0。
 * jsdom 系の test 等) では、 全行を描く。
 */
export const useVirtualRows = (ids: readonly string[]): VirtualRows => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const heights = useRef(new Map<string, number>())
  const [viewport, setViewport] = useState({
    scrollTop: 0,
    height: INITIAL_VIEWPORT_HEIGHT,
  })
  const [hasNoLayout, setHasNoLayout] = useState(false)
  const [measuredVersion, setMeasuredVersion] = useState(0)

  const readViewport = useCallback((): void => {
    const el = scrollRef.current
    if (el === null) return
    if (el.clientHeight === 0) {
      setHasNoLayout(true)
      return
    }
    setHasNoLayout(false)
    setViewport((prev) =>
      prev.scrollTop === el.scrollTop && prev.height === el.clientHeight
        ? prev
        : { scrollTop: el.scrollTop, height: el.clientHeight },
    )
  }, [])

  useLayoutEffect(() => {
    readViewport()
  }, [readViewport])

  useEffect(() => {
    const el = scrollRef.current
    if (el === null || typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(readViewport)
    observer.observe(el)
    return () => {
      observer.disconnect()
    }
  }, [readViewport])

  const measure = useCallback((id: string, height: number): void => {
    const prev = heights.current.get(id)
    if (prev !== undefined && Math.abs(prev - height) < 1) return
    heights.current.set(id, height)
    setMeasuredVersion((v) => v + 1)
  }, [])

  const win = useMemo(() => {
    if (hasNoLayout) {
      return { start: 0, end: ids.length, topPad: 0, bottomPad: 0 }
    }
    const measured = [...heights.current.values()]
    const estimate =
      measured.length === 0
        ? DEFAULT_ROW_HEIGHT
        : measured.reduce((sum, h) => sum + h, 0) / measured.length
    return computeVirtualWindow(
      ids.length,
      (i) => heights.current.get(ids[i] ?? '') ?? estimate,
      viewport.scrollTop,
      viewport.height,
      OVERSCAN_ROWS,
    )
    // measuredVersion は heights (ref) の更新を再計算に伝えるための依存。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNoLayout, ids, viewport, measuredVersion])

  return { scrollRef, onScroll: readViewport, ...win, measure }
}
